(ns ncr.fragment.props
  (:require
   [cljs.core.async :as async :refer-macros [go] :refer [<!]]
   [goog.dom :as gd]
   [goog.fx.easing :as easing]
   [goog.math :as gm]
   [lambdaisland.glogi :as log]
   [uix.core.alpha :as uix]
   [ncr.etc.hooks :as h]
   [ncr.etc.pf :as pf]
   [ncr.etc.pubsub :as ps]
   [ncr.fragment.common :as fc]
   [ncr.fragment.attachment-markup :as am]
   [ncr.fragment.props.money.input :as mi]
   [ncr.fragment.props.money.property-input :as money-inputs]
   [ncr.fragment.props.date :as props-date]
   [ncr.fragment.props.timesheet :as props-timesheet]
   [ncr.fragment.props.estimates :as props-estimate]))

(defn- c-section [{:keys [title]} & content]
  (let
    [toggle (uix/memo #(gensym "toggle") [])
     expanded?
     @(ps/sub-reducer-map! true
        {toggle not})]
    [pf/c-expandable-section
     {:is-expanded expanded?
      :toggle-text title
      :on-toggle
      #(ps/pub! [toggle])}
     (into [:<>] content)]))

(defn- c-property [p control key {:keys [fragment properties]}]
  [control (assoc p :property-key key) (get properties key)
   #(fc/update-props {key %1} %2 (get fragment :id)) {:fragment-name (:name fragment)}])

(defn- get-update-form [ratio]
  (let [label-column-width (str (+ 4.375 (* ratio 4)) "rem") ; default 9.375rem
        ]
    (fn [node]
      (let [^js style (.-style node)]
        (.setProperty style "--pf-c-form--m-horizontal__group-label--md--GridColumnWidth" label-column-width)))))

(defn- get-update-drawer-body [ratio]
  (let [padding (str (+ 0.5 (* ratio 1.0)) "rem") ; default 1.5rem
        ]
    (fn [node]
      (let [^js style (.-style node)]
        (.setProperty style "--pf-c-drawer--child--PaddingRight" padding)
        (.setProperty style "--pf-c-drawer--child--PaddingLeft" padding)))))

(defn- update-forms-width [panel ratio]
  (let [update-form (get-update-form ratio)
        update-drawer-body (get-update-drawer-body ratio)]
    (doseq [form (gd/getElementsByClass "pf-c-form" panel)]
      (update-form form))
    (doseq [body (gd/getElementsByClass "pf-c-drawer__body" panel)]
      (update-drawer-body body))))

(defn- get-ratio-fn [resizeable? min-width max-width]
  (if resizeable?
    (let [delta (- max-width min-width)]
      (fn [w]
        (easing/easeIn (gm/clamp (/ (- w min-width) delta) 0 1))))
    (constantly 1)))

(defn c-properties-drawer [{:keys [model width max-width min-width set-width]
                            :or {min-width 350 max-width 600}}]
  (let [*panel (uix/state nil)
        id (str (gensym))
        width (gm/clamp (or width max-width) min-width max-width)
        resizeable? (h/use-media-query "screen and (max-width: 1680px)")
        get-ratio (get-ratio-fn resizeable? min-width max-width)
        panel @*panel
        *actual-width (uix/state width)
        actual-width @*actual-width
        *wrapping-width (uix/state (dec width))
        wrapping-width @*wrapping-width
        horizontal? (> actual-width wrapping-width)]

    ; this is a hack to work around NCR-611, hopefully properties panel will be gone soon, and we can remove this
    (uix/effect!
      (fn []
        (when-let [^js main (and panel horizontal? (gd/getElementByClass "pf-c-drawer__panel-main" panel))]
          (when (> (.-scrollWidth main) (.-clientWidth main))
            (reset! *wrapping-width actual-width))))
      [panel wrapping-width actual-width horizontal?])

    (uix/effect!
      (fn []
        (when-let [node panel]
          (let [^js resize-observer
                (new js/ResizeObserver
                  (fn [^js entries _observer]
                    (let [^js entry (last entries)
                          width (.. entry -contentRect -width)]
                      (reset! *actual-width width)
                      (update-forms-width panel (get-ratio width)))))]
            (.observe resize-observer node)
            #(.disconnect resize-observer))))
      [panel])

    (uix/effect!
      (fn []
        (when-let [node (and (nil? panel) (gd/getElement id))]
          (reset! *panel node)))
      [])

    [pf/c-drawer-panel-content
     (merge
       {:id id
        :class :ncr-c-drawer-panel__properties}
       (when resizeable?
         {:is-resizable true
          :default-size (str width "px")
          :max-size (str max-width "px")
          :min-size (str min-width "px")
          :on-resize set-width}))
     [pf/c-drawer-head [:h3.pf-u-font-size-lg "Properties"]]
     [pf/c-drawer-panel-body
      [pf/c-stack
       [pf/c-stack-item
        {:is-filled true}
        [c-section {:title "Common"}
         [pf/c-form
          {:is-horizontal horizontal?
           :on-submit #(.preventDefault %)}
          (let [{:keys [properties]} model]
            [money-inputs/c-netto-brutto
             {:label "Value"}
             {:netto (:common/netto properties)
              :brutto (:common/brutto properties)}
             (fn [{:keys [netto brutto]}]
               (fc/update-props {:common/netto netto :common/brutto brutto}
                 "Netto/Brutto" (get-in model [:fragment :id])))])
          [c-property {:label "Date"}
           props-date/c-date-property :common/date model]]]
        [c-section {:title "Experimental"}
         [pf/c-form {:is-horizontal horizontal?}
          [c-property {:label "Estimates"}
           props-estimate/c-estimate-property :estimate/data model]]
         [pf/c-form {:is-horizontal horizontal?}
          [c-property {:label "Timesheet"}
           props-timesheet/c-timesheet-property props-timesheet/PROPERTY_KEY model]]]]
       [pf/c-stack-item
        [am/properties-view]]]]]))

(defn- c-property-label [class body]
  [pf/c-list-item {:class class}
   [pf/c-label
    {:variant :outline
     :class [:pf-u-font-size-sm :pf-u-py-0 :ncr-c-prop-label]}
    body]])

(defn c-properties-line [{:keys [blurred? fragment-id]} props]
  (let
    [netto-value (get props :common/netto)
     brutto-value (get props :common/brutto)
     date (get props :common/date)
     timesheet (get props props-timesheet/PROPERTY_KEY)
     estimate (get props :estimate/data)
     show-netto-brutto? (or netto-value brutto-value)]

    (when (or show-netto-brutto? date timesheet estimate)
      [pf/c-list {:variant :inline :class :ncr-c-prop-list}

       (when show-netto-brutto?
         [:li {:class [:ncr-c-prop-label--money_netto-brutto (when blurred? :ncr-c-prop-label--blurred)]}
          [mi/c-inline-input-netto-brutto
           {:netto netto-value
            :brutto brutto-value
            :ignore-missing? false
            :display-brutto :both
            :display-netto :absolute
            :display-type :label
            :on-change (fn [{:keys [netto brutto]}]
                         (go
                           (<! (fc/update-props {:common/netto netto :common/brutto brutto}
                                 "Netto/Brutto" fragment-id))
                           (ps/pub! [:search/execute false])))}]])

       (when date
         [c-property-label :ncr-c-prop-label--date
          [props-date/c-date-label date]])

       (when timesheet
         [c-property-label :ncr-c-prop-label--timesheet
          [props-timesheet/c-timesheet-label timesheet]])

       (when estimate
         [c-property-label :ncr-c-prop-label--estimate
          [props-estimate/c-estimate-label estimate]])])))
